<template>
  <div class="block p-buttonset">
    <div class="flex justify-content-between flex-wrap">
      <label
        class="block my-3"
        data-test-id="image-label"
      >
        {{ t(configs.label) }}
      </label>
      <div class="flex justify-content-center align-items-center">
        <ToggleDisplaySettings
          v-if="!Object.prototype.hasOwnProperty.call(configs, 'displayToggle') || configs.displayToggle"
          :configs="configs"
          :parser-values="parserValues"
          :disabled="disableHandler ? disableHandler() : false"
          :persist-disabled-state="persistDisabledState"
          @display-settings="(value) => handleToggleChange(value)"
        />
      </div>
    </div>
    <div
      v-if="disableHandler && disableHandler() && disableMessage"
      class="spm-field-disabled-notice mb-2"
    >
      {{ t(disableMessage) }}
    </div>
    <div
      class="text-center mb-2"
      :class="{ 'hidden': !displayProperties || (disableHandler && disableHandler()) }"
    >
      <Button
        v-if="isRemoved"
        :label="t('templateBuilder.fields.chooseImage')"
        class="mb-2"
        data-test-id="image-button"
        @click="displayImageModal=true"
      />
      <div
        v-if="!isRemoved"
        class="p-fluid flex mb-2"
      >
        <InputText
          :id="inputUniqueId"
          v-model="selectedImage"
          class="input-text"
          data-test-id="image-input"
          :placeholder="t('templateBuilder.fields.imageUrl')"
          @change="handleChange"
        />
      </div>
      <div
        v-if="!isRemoved"
        class="text-center"
      >
        <div class="mt-2 p-2 selected-image">
          <div class="preview-container">
            <img
              v-if="selectedImage"
              :src="previewImageSrc"
              alt="alt"
              class="product-image"
            >
            <div class="action-buttons">
              <Button
                v-tooltip.bottom="t('templateBuilder.fields.replaceImage')"
                icon="far fa-images"
                class="mt-2 mr-2"
                data-test-id="image-button"
                @click="displayImageModal=true"
              />
              <Button
                v-if="selectedImage"
                v-tooltip.bottom="t('templateBuilder.fields.editImage')"
                icon="far fa-crop-simple"
                class="mt-2 mr-2"
                @click="editImageModal=true"
              />
              <span v-tooltip.bottom="t('templateBuilder.fields.generateImageAi')">
                <AIButton
                  type="button"
                  class="mt-2 mr-2 input-button-ai"
                  icon="far fa-light fa-microchip-ai"
                  :ai-selector="parserValues.selector"
                  :ai-params="{ target: 'template', type: 'attr', 'attr': 'src', 'updateFieldWithValue': { 'selector': '#' + inputUniqueId } }"
                  ai-content-type="3"
                />
              </span>
              <Button
                v-if="configs.options.remove"
                v-tooltip.bottom="t('templateBuilder.fields.removeImage')"
                icon="far fa-trash"
                class="mt-2"
                @click="handleRemove"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :class="{ 'hidden': !displayProperties }">
      <!-- Colorpicker for monochrome images -->
      <div
        v-if="isMonochromeImage && configs.options.description"
        class="mt-2"
      >
        <Color
          :configs="colorFieldConfig"
          :parser-values="colorParserValues"
          @on-change-properties="handleColorChangeFromColor"
        />
        <small class="monochrome-hint">
          {{ t('templateBuilder.fields.imageColorHint') }}
        </small>
      </div>
      <div
        v-if="configs.options.description"
        class="mt-2"
      >
        <label class="block my-3">
          {{ t('templateBuilder.fields.imageDescription') }}
        </label>
        <div class="p-fluid flex mb-1">
          <InputText
            v-model="description"
            class="input-text"
            @input="handleChangeDescription"
          />
        </div>
      </div>
      <FileManager
        v-if="displayImageModal"
        :display-dialog="displayImageModal"
        extensions="image/*"
        @on-close-dialog="displayImageModal=false"
        @on-choose-file="onChooseImage"
      />
      <EditImageModal
        :display-dialog="editImageModal"
        :image="selectedImage"
        @on-close-dialog="editImageModal=false"
        @on-save-image="onSaveImage"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, Ref, ref,
  watch, onMounted, computed,
} from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Tooltip from 'primevue/tooltip';
import FileManager from '@/components/file-manager/FileManager.vue';
import EditImageModal from '@/components/modals/EditImageModal.vue';
import Color from '@/components/template-builder/fields/Color.vue';
import {
  detectMonochromeImageCached,
  clearMonochromeDetectionCache,
  MonochromeDetectionResult,
  getColorizedUrl,
  getOriginalUrl,
  getColorizedUrlInfo,
  isColorizableUrl,
} from '../utils/image-color-detection';
import {
  FieldConfig,
  File,
  ParserFieldObject,
  Property,
} from '@/types';
import { useI18n } from 'vue-i18n';
import AIButton from '@/components/fields/AIButton.vue';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';
import InlineMessage from 'primevue/inlinemessage';
import { removeImage, showImage } from '../callbacks';
import { isVariableExpression, resolveVariableSync, getVariablePlaceholderUrl } from '../utils/variable-image';

export default defineComponent({
  name: 'Image',

  components: {
    FileManager,
    AIButton,
    EditImageModal,
    Button,
    InputText,
    ToggleDisplaySettings,
    InlineMessage,
    Color,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    disableHandler: {
      type: Function,
      required: false,
      default: null,
    },

    disableMessage: {
      type: String,
      required: false,
      default: '',
    },

    persistDisabledState: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const displayImageModal = ref(false);
    const editImageModal = ref(false);
    const srcProperty: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties.filter((property) => property.name === 'src')[0]);
    const selectedImage: Ref<string> = ref(srcProperty.value.value ?? props.configs.options.defaultImageUrl);
    const altProperty: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties.filter((property) => property.name === 'alt')[0]);
    const description: Ref<string> = ref((altProperty.value && altProperty.value.value) ?? '');
    const showImageProperty: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties.filter((property) => property.name === 'data-show-image')[0]);
    const isRemoved: Ref<boolean> = ref(false);
    const configs = ref(props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configs.value, 'displayToggle') || configs.value.displayToggle === true));

    const previewImageSrc = computed(() => {
      if (selectedImage.value && isVariableExpression(selectedImage.value)) {
        const resolved = resolveVariableSync(selectedImage.value);
        return resolved || getVariablePlaceholderUrl();
      }
      return selectedImage.value;
    });

    const handleChange = () => {
      srcProperty.value.value = selectedImage.value;
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [srcProperty.value],
      });
    };

    const handleDisplayImageChange = () => {
      if (!showImageProperty.value) {
        return;
      }
      showImageProperty.value.value = displayProperties.value;
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [showImageProperty.value],
      });
    };

    const handleChangeDescription = () => {
      altProperty.value.value = description.value;
      if (displayProperties.value) {
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [altProperty.value],
        });
      }
    };

    const handleRemove = () => {
      selectedImage.value = '';
      removeImage(props.parserValues.selector);
      isRemoved.value = true;
      handleChange();
    };

    // Pending color to re-apply after monochrome detection confirms
    const pendingColor: Ref<string | null> = ref(null);

    const onChooseImage = (image: File) => {
      // Save current color to re-apply if new image turns out to be monochrome
      if (isMonochromeImage.value && monochromeColor.value && monochromeColor.value !== '#000000') {
        pendingColor.value = monochromeColor.value;
      }

      selectedImage.value = image.url;
      handleChange();
      if (props.configs.options.remove) {
        isRemoved.value = false;
        showImage(props.parserValues.selector);
      }
    };

    const onSaveImage = (imageUrl: string) => {
      selectedImage.value = imageUrl;
      handleChange();
    };

    const handleToggleChange = (value: boolean) => {
      displayProperties.value = value;
      isRemoved.value = !value;
    };

    const createUniqueString = () => Math.random().toString(36).substring(2, 15) + Date.now().toString(36);

    // Monochrome detection state
    const isMonochromeImage: Ref<boolean> = ref(false);

    // Initialize color from existing colorized URL (synchronous)
    const getInitialColor = (): string => {
      if (selectedImage.value) {
        const colorInfo = getColorizedUrlInfo(selectedImage.value);
        if (colorInfo.isAlreadyColorized && colorInfo.currentColor) {
          return colorInfo.currentColor;
        }
      }
      return '#000000';
    };
    const monochromeColor: Ref<string> = ref(getInitialColor());

    // Configuration for Color.vue component (like SocialIconsStyleV4.vue)
    const colorFieldConfig = computed(() => ({
      label: 'templateBuilder.fields.imageColor',
      displayToggle: false,
      options: {
        defaultValue: monochromeColor.value,
        showAlpha: false,
        showGradient: false,
      },
      properties: [{
        name: 'imageColor',
        value: monochromeColor.value,
        getter: () => monochromeColor.value,
        setters: [],
      }],
    }));

    const colorParserValues = computed(() => ({
      selector: props.parserValues.selector,
      properties: [{
        name: 'imageColor',
        value: monochromeColor.value,
        getter: () => monochromeColor.value,
        setters: [],
      }],
    }));

    const handleColorChangeFromColor = (event: { properties: Property[] }) => {
      if (event.properties && event.properties[0]) {
        const newColor = event.properties[0].value as string;

        // Only accept 6-character hex colors (e.g., #FF0000)
        const hex6Regex = /^#[A-Fa-f0-9]{6}$/;
        if (!hex6Regex.test(newColor)) {
          return;
        }

        monochromeColor.value = newColor;

        // Generate colorized URL and apply it
        const colorizedUrl = getColorizedUrl(selectedImage.value, newColor);
        if (colorizedUrl) {
          selectedImage.value = colorizedUrl;
          handleChange();
        }
      }
    };

    const analyzeImage = async (imageUrl: string) => {
      if (!imageUrl || imageUrl.includes('placeholder') || isVariableExpression(imageUrl)) {
        isMonochromeImage.value = false;
        return;
      }

      // Check if URL is colorizable first
      if (!isColorizableUrl(imageUrl)) {
        isMonochromeImage.value = false;
        return;
      }

      // Get original URL for detection (in case it's already colorized)
      const originalUrl = getOriginalUrl(imageUrl);
      const colorInfo = getColorizedUrlInfo(imageUrl);

      // If already colorized, use the existing color
      if (colorInfo.isAlreadyColorized && colorInfo.currentColor) {
        monochromeColor.value = colorInfo.currentColor;
      }

      try {
        const result: MonochromeDetectionResult = await detectMonochromeImageCached(originalUrl);
        isMonochromeImage.value = result.isMonochrome;
        if (result.isMonochrome && !colorInfo.isAlreadyColorized) {
          // Re-apply pending color from previous monochrome image
          if (pendingColor.value) {
            monochromeColor.value = pendingColor.value;
            const colorizedUrl = getColorizedUrl(imageUrl, pendingColor.value);
            if (colorizedUrl) {
              selectedImage.value = colorizedUrl;
              handleChange();
            }
            pendingColor.value = null;
          } else if (result.detectedColor) {
            monochromeColor.value = result.detectedColor;
          }
        } else {
          pendingColor.value = null;
        }
      } catch (error) {
        console.warn('[Image] Monochrome detection failed:', error);
        isMonochromeImage.value = false;
        pendingColor.value = null;
      }
    };

    // Analyze image on mount and when image changes
    onMounted(() => {
      if (selectedImage.value) {
        analyzeImage(selectedImage.value);
      }
    });

    watch(() => selectedImage.value, (newUrl) => {
      if (newUrl) {
        clearMonochromeDetectionCache(newUrl);
        analyzeImage(newUrl);
      } else {
        isMonochromeImage.value = false;
      }
    });

    watch(() => displayProperties.value, () => {
      // setTimeout to avoids undesirable visual effects
      setTimeout(async () => {
        handleDisplayImageChange();
        handleChange();
      }, 100);
    }, { deep: true });

    return {
      t,
      displayImageModal,
      editImageModal,
      isRemoved,
      description,
      onChooseImage,
      handleChangeDescription,
      onSaveImage,
      handleChange,
      handleRemove,
      handleToggleChange,
      selectedImage,
      previewImageSrc,
      inputUniqueId: `unique-input-${createUniqueString()}`,
      displayProperties,
      disableHandler: props.disableHandler,
      disableMessage: props.disableMessage,
      persistDisabledState: props.persistDisabledState,
      // Monochrome detection
      isMonochromeImage,
      colorFieldConfig,
      colorParserValues,
      handleColorChangeFromColor,
    };
  },
});
</script>

<style scoped lang="scss">
$img-size: 11rem;
.selected-image {
  width: 100%;
  margin: auto;
  border: 1px solid $solitude !important;
  text-align: center;

  .preview-container {
    position: relative;
    text-align: center;

    .product-image {
      width: $img-size;
      object-fit: cover;
      object-position: bottom;
      margin: auto;
    }

    .action-buttons {
      display: block;

      & .p-button {
        border: solid 1px $solitude;
        background: $white;
        color: $heather;
        border-radius: 3px;
        box-shadow: none !important;
      }
    }
  }
}
.input-text {
  font-size: 1.25rem !important;
  padding: 0.625rem 0.625rem !important;
}

.input-button-ai {
  width:2.357rem!important;
  padding:3px 0!important;
  height:auto!important;
}

.monochrome-hint {
  color: $heather;
  font-size: 0.85rem;
  display: block;
  margin-top: 0.25rem;
}

</style>
